import { InvitedProps } from "@pages/invited/invited-types";
import { InvitedSubtitle } from "@pages/invited/invited-subtitle";
import { SignupBackButton } from "@components/signup/signup-back-button";
import { useState } from "react";
import { UlmButton } from "@components/ulm/ulm-button";
import { faroCoreApiClient } from "@api/core-api/core-api-utils";
import { runtimeConfig } from "@src/runtime-config";
import { useErrorHandlerContext } from "@context-providers/error-handler/error-handler-context";
import { useAppSelector } from "@store/store-helper";
import { ulmConfigClientSelector } from "@store/ulm-config/ulm-config-selector";
import { redirectClient } from "@utils/client-utils";
import { SignupForm } from "@components/signup/signup-form/signup-form";
import { SignupNewsletterCheckbox } from "@components/signup/signup-newsletter-checkbox";
import { useSignupForm } from "@components/signup/signup-form/use-signup-form";

/**
 * Renders the second step of the invited user registration flow.
 * This step is required for users that select email/password authentication method.
 */
export function InvitedStepTwo({ setStep, email }: InvitedProps): JSX.Element {
  const { handleError } = useErrorHandlerContext();
  const client = useAppSelector(ulmConfigClientSelector);
  const { form, setForm, errors, setErrors, setIsPasswordValid, validateForm } =
    useSignupForm();

  const [shouldSubscribeToNewsletter, setShouldSubscribeToNewsletter] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /** If the form is valid, registers the user and redirects to the workspace */
  async function onClick(): Promise<void> {
    setIsLoading(true);

    const isFormValid = validateForm();

    if (isFormValid) {
      try {
        await faroCoreApiClient.users.extendedRegister({
          /* eslint-disable @typescript-eslint/naming-convention -- defined on the backend */
          rgst_email: email,
          rgst_first_name: form.firstName.trim(),
          rgst_last_name: form.lastName.trim(),
          rgst_password: form.password,
          /* eslint-enable @typescript-eslint/naming-convention -- defined on the backend */
          newsletter: shouldSubscribeToNewsletter ? "on" : "off",
        });
        redirectClient({
          client,
          redirectUrl: runtimeConfig.urls.sphereDashboardUrl,
        });
      } catch (error) {
        handleError({
          id: `extendedRegister-${Date.now().toString()}`,
          title: "Failed to activate the account.",
          error,
          uiType: "toast",
        });
      }
    }

    setIsLoading(false);
  }

  return (
    <>
      <InvitedSubtitle
        sx={{
          marginBottom: "16px",
        }}
      />
      <SignupForm
        form={form}
        setForm={setForm}
        errors={errors}
        setErrors={setErrors}
        setIsPasswordValid={setIsPasswordValid}
      />
      <SignupNewsletterCheckbox
        shouldSubscribeToNewsletter={shouldSubscribeToNewsletter}
        setShouldSubscribeToNewsletter={setShouldSubscribeToNewsletter}
      />
      <UlmButton
        onClick={onClick}
        isLoading={isLoading}
        isLoadingText="Activating..."
        sx={{
          marginY: "16px",
        }}
      >
        Get Started
      </UlmButton>
      <SignupBackButton
        handleBack={() => setStep("one")}
        isLoading={isLoading}
      />
    </>
  );
}
